import { AGENT_OS_LABELS, AGENT_UPDATE_CHANNEL_LABELS, type AgentDetail } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Ban } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  adapterHealthSummary,
  adapterHealthTone,
  formatBytes,
  formatClockSkew,
  formatUptime,
  heartbeatMetrics,
  isClockSkewHigh,
} from "../../../features/agents/agent-values.js";
import { RevokeAgentDialog } from "../../../features/agents/command-dialogs.js";
import {
  AdaptersTab,
  AlarmsTab,
  CommandsTab,
  HeartbeatsTab,
  LogUploadsTab,
} from "../../../features/agents/detail-tabs.js";
import { useAgent, useAgentPermissions } from "../../../features/agents/queries.js";
import {
  AgentsPage,
  AgentStatusBadge,
  Field,
  OnlineBadge,
  RelativeTime,
} from "../../../features/agents/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

const DETAIL_TABS = ["adapters", "commands", "heartbeats", "alarms", "logs"] as const;
type DetailTab = (typeof DETAIL_TABS)[number];

const searchSchema = z.object({ tab: z.enum(DETAIL_TABS).optional().catch(undefined) });

export const Route = createFileRoute("/_app/ajanlar/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: AgentDetailRoute,
});

/**
 * Ajan ayrıntısı: başlık kartı (kimlik, sürüm, kuyruk, saat sapması, disk / bellek) + sekmeler
 * (adaptörler ve cihazlar, komutlar, heartbeat geçmişi, alarmlar, günlük yüklemeleri).
 *
 * Ölçüler 30 saniyede bir tazelenir; gerçek zamanlı `agents.*` olayları bunun üstüne gelir.
 */
function AgentDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useAgentPermissions();
  const agent = useAgent(id, permissions.enabled && permissions.canRead);
  const title = agent.data?.hostname ?? t("agents.detail.title");

  return (
    <AgentsPage
      title={title}
      breadcrumb={[
        {
          label: t("menu.agents"),
          link: (children, className) => (
            <Link to="/ajanlar" className={className}>
              {children}
            </Link>
          ),
        },
        { label: title },
      ]}
    >
      {() =>
        agent.error ? (
          <Alert tone="danger">{errorMessage(agent.error)}</Alert>
        ) : !agent.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <AgentDetailBody agent={agent.data as AgentDetail} />
        )
      }
    </AgentsPage>
  );
}

function AgentDetailBody({ agent }: { agent: AgentDetail }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useAgentPermissions();
  const tab: DetailTab = search.tab ?? "adapters";
  const [revoking, setRevoking] = useState(false);

  const health = adapterHealthSummary(agent.adapters);
  const metrics = heartbeatMetrics(agent.lastHeartbeat);

  return (
    <div className="grid gap-4">
      <Card className="grid gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-lg font-semibold">{agent.hostname}</span>
          <AgentStatusBadge status={agent.status} />
          <OnlineBadge online={agent.online} />
          {isClockSkewHigh(agent.clockSkewMs) ? (
            <StatusBadge tone="warning">{t("agents.clockSkewHigh")}</StatusBadge>
          ) : null}
          <StatusBadge tone={adapterHealthTone(health)}>
            {health.total === 0
              ? t("agents.adapters.none")
              : health.failing.length === 0
                ? t("agents.adapters.allOk", { ok: health.ok, total: health.total })
                : t("agents.adapters.failing", {
                    names: health.failing.map((adapter) => adapter.id).join(", "),
                  })}
          </StatusBadge>
          <span className="text-sm text-muted-foreground">
            <span className="font-mono">{agent.stationCode}</span> {agent.stationName}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label={t("agents.fields.os")}>
            {AGENT_OS_LABELS[agent.os]} · {agent.arch}
          </Field>
          <Field label={t("agents.fields.version")}>
            <span className="font-mono">{agent.version}</span>
            {metrics.commit ? (
              <span className="text-xs text-muted-foreground"> ({metrics.commit})</span>
            ) : null}
          </Field>
          <Field label={t("agents.fields.updateChannel")}>
            {AGENT_UPDATE_CHANNEL_LABELS[agent.updateChannel]}
          </Field>
          <Field label={t("agents.fields.enrolledAt")}>{formatDateTime(agent.enrolledAt)}</Field>
          <Field label={t("agents.fields.lastSeenAt")}>
            <RelativeTime value={agent.lastSeenAt} />
          </Field>
          <Field label={t("agents.fields.lastSyncAt")}>
            <RelativeTime value={agent.lastSyncAt} />
          </Field>
          <Field label={t("agents.fields.clockSkew")}>{formatClockSkew(agent.clockSkewMs)}</Field>
          <Field label={t("agents.fields.uptime")}>{formatUptime(metrics.uptimeSeconds)}</Field>
          <Field label={t("agents.fields.queueDepth")}>{formatCount(agent.queueDepth)}</Field>
          <Field label={t("agents.fields.deadLetterCount")}>{formatCount(metrics.deadLetterCount)}</Field>
          <Field label={t("agents.fields.oldestPendingAt")}>
            {agent.oldestPendingAt === null ? (
              t("agents.summary.queueEmpty")
            ) : (
              <RelativeTime value={agent.oldestPendingAt} />
            )}
          </Field>
          <Field label={t("agents.fields.lastAckedSeq")}>
            <span className="tabular">{agent.lastAckedSeq}</span>
          </Field>
          <Field label={t("agents.fields.disk")}>
            {metrics.diskFreeBytes === null || metrics.diskTotalBytes === null
              ? "—"
              : t("agents.fields.diskValue", {
                  free: formatBytes(metrics.diskFreeBytes),
                  total: formatBytes(metrics.diskTotalBytes),
                })}
          </Field>
          <Field label={t("agents.fields.memory")}>{formatBytes(metrics.memoryRssBytes)}</Field>
          <Field label={t("agents.fields.pendingCommands")}>{formatCount(agent.pendingCommands)}</Field>
          <Field label={t("agents.fields.lastHeartbeatAt")}>
            <RelativeTime value={agent.lastHeartbeatAt} />
          </Field>
        </dl>

        {agent.status === "REVOKED" ? (
          <Alert tone="warning" title={t("agents.revoke.revokedTitle")}>
            {t("agents.revoke.revokedDescription", {
              at: formatDateTime(agent.revokedAt),
              reason: agent.revokeReason ?? "—",
            })}
          </Alert>
        ) : null}

        {permissions.canManage && agent.status === "ACTIVE" ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setRevoking(true)}>
              <Ban aria-hidden="true" />
              {t("agents.revoke.title")}
            </Button>
          </div>
        ) : null}
      </Card>

      <Tabs
        className="grid gap-4"
        value={tab}
        onValueChange={(next) =>
          void navigate({
            to: ".",
            search: ((previous: Record<string, unknown>) => ({ ...previous, tab: next })) as never,
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("agents.detail.tabs")}>
          {DETAIL_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`agents.detail.tab.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* Etkin sekmenin paneli: tetikleyicinin `aria-controls` hedefi var olmalı (axe: aria-valid-attr-value). */}
        <TabsContent value={tab} className="grid gap-4 pt-0">
          {tab === "adapters" ? <AdaptersTab agent={agent} /> : null}
          {tab === "commands" ? <CommandsTab agent={agent} canCommand={permissions.canCommand} /> : null}
          {tab === "heartbeats" ? <HeartbeatsTab agentId={agent.id} /> : null}
          {tab === "alarms" ? <AlarmsTab agentId={agent.id} /> : null}
          {tab === "logs" ? <LogUploadsTab agentId={agent.id} /> : null}
        </TabsContent>
      </Tabs>

      {permissions.canManage ? (
        <RevokeAgentDialog open={revoking} onOpenChange={setRevoking} agent={agent} />
      ) : null}
    </div>
  );
}
